/**
 * Equipment register (F4.1, R30): scales, moisture meters and test weights with calibration
 * and Legal Metrology stamp dates, and processing machines (F6.9) with the steps they run.
 * Limits follow uc-core's Create/UpdateEquipmentDto and EquipmentService.checkMachine.
 */

export const GATE_KINDS = ['SCALE', 'MOISTURE_METER', 'TEST_WEIGHT'] as const
export type GateKind = (typeof GATE_KINDS)[number]
export const GATE_KIND_LABEL: Record<GateKind, string> = {
  SCALE: 'Scale',
  MOISTURE_METER: 'Moisture meter',
  TEST_WEIGHT: 'Test weight',
}

/** A processing machine: category PROCESSING, no gate kind, steps in specifications.step_codes. */
export const MACHINE = 'MACHINE' as const
export type EquipmentKind = GateKind | typeof MACHINE
export const EQUIPMENT_KINDS: readonly EquipmentKind[] = [...GATE_KINDS, MACHINE]
export const EQUIPMENT_KIND_LABEL: Record<EquipmentKind, string> = { ...GATE_KIND_LABEL, MACHINE: 'Processing machine' }

/** uc-core's machine code rule (the run code embeds it). */
export const MACHINE_CODE_RE = /^[A-Z0-9-]{1,8}$/

type Specifications = Record<string, unknown>

/** The machine's steps from equipment.specifications.step_codes (non-strings dropped). */
export function stepCodesOf(spec: Specifications | null | undefined): string[] {
  const v = spec?.step_codes
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []
}

/** A machine as the phones see it: category PROCESSING or a step_codes list (machine.service list). */
export const isMachine = (e: { category?: string | null; specifications?: Specifications | null }) =>
  e.category === 'PROCESSING' || Array.isArray(e.specifications?.step_codes)

export interface EquipmentForm {
  code: string
  name: string
  gateKind: EquipmentKind | ''
  serialNo: string
  calibratedAt: string
  calibrationDueAt: string
  legalMetrologyCertNo: string
  stampedAt: string
  stampDueAt: string
  locationId: string
  sharedUse: boolean
  isActive: boolean
  /** Machines: the steps the phones offer it for (specifications.step_codes). */
  stepCodes: string[]
}

export interface EquipmentLike {
  code: string
  name: string
  gateKind: string | null
  serialNo: string | null
  calibratedAt: string | null
  calibrationDueAt: string | null
  legalMetrologyCertNo: string | null
  stampedAt: string | null
  stampDueAt: string | null
  locationId: string | null
  sharedUse: boolean
  isActive: boolean
  category?: string | null
  specifications?: Specifications | null
}

export const emptyEquipmentForm = (): EquipmentForm => ({
  code: '',
  name: '',
  gateKind: 'SCALE',
  serialNo: '',
  calibratedAt: '',
  calibrationDueAt: '',
  legalMetrologyCertNo: '',
  stampedAt: '',
  stampDueAt: '',
  locationId: '',
  sharedUse: true,
  isActive: true,
  stepCodes: [],
})

const day = (v: string | null) => (v ? v.slice(0, 10) : '')

export function equipmentFormOf(e: EquipmentLike): EquipmentForm {
  return {
    code: e.code,
    name: e.name,
    gateKind: (GATE_KINDS as readonly string[]).includes(e.gateKind ?? '') ? (e.gateKind as GateKind) : isMachine(e) ? MACHINE : '',
    serialNo: e.serialNo ?? '',
    calibratedAt: day(e.calibratedAt),
    calibrationDueAt: day(e.calibrationDueAt),
    legalMetrologyCertNo: e.legalMetrologyCertNo ?? '',
    stampedAt: day(e.stampedAt),
    stampDueAt: day(e.stampDueAt),
    locationId: e.locationId ?? '',
    sharedUse: e.sharedUse,
    isActive: e.isActive,
    stepCodes: stepCodesOf(e.specifications),
  }
}

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/
const validDay = (v: string) => ISO_DAY.test(v) && !Number.isNaN(Date.parse(`${v}T00:00:00Z`))

export type EquipmentFormErrors = Partial<Record<keyof EquipmentForm, string>>

export function validateEquipmentForm(f: EquipmentForm, isNew: boolean): EquipmentFormErrors {
  const e: EquipmentFormErrors = {}
  const code = f.code.trim()
  if (!code) e.code = 'Required'
  else if (code.length > 50) e.code = '50 characters at most'
  else if ((f.gateKind === MACHINE || f.stepCodes.length > 0) && !MACHINE_CODE_RE.test(code))
    e.code = 'A machine code is at most 8 characters: A–Z, 0–9 and -'
  const name = f.name.trim()
  if (!name) e.name = 'Required'
  else if (name.length > 255) e.name = '255 characters at most'
  if (isNew && !f.gateKind) e.gateKind = 'Choose what it is'
  if (isNew && f.gateKind === MACHINE && f.stepCodes.length === 0)
    e.stepCodes = 'Choose at least one step — the phones offer the machine only for these'
  if (f.serialNo.trim().length > 60) e.serialNo = '60 characters at most'
  if (f.legalMetrologyCertNo.trim().length > 40) e.legalMetrologyCertNo = '40 characters at most'
  for (const k of ['calibratedAt', 'calibrationDueAt', 'stampedAt', 'stampDueAt'] as const) {
    if (f[k] && !validDay(f[k])) e[k] = 'Not a date'
  }
  if (!e.calibrationDueAt && f.calibratedAt && f.calibrationDueAt && f.calibrationDueAt < f.calibratedAt)
    e.calibrationDueAt = 'Due date is before the calibration date'
  if (!e.stampDueAt && f.stampedAt && f.stampDueAt && f.stampDueAt < f.stampedAt)
    e.stampDueAt = 'Due date is before the stamp date'
  return e
}

export type EquipmentBody = {
  code?: string
  name?: string
  category?: 'MEASUREMENT' | 'PROCESSING'
  gateKind?: GateKind
  serialNo?: string | null
  calibratedAt?: string | null
  calibrationDueAt?: string | null
  legalMetrologyCertNo?: string | null
  stampedAt?: string | null
  stampDueAt?: string | null
  locationId?: string
  sharedUse?: boolean
  isActive?: boolean
  specifications?: Specifications
}

/**
 * The specifications to send for the chosen steps, merged into what the equipment has (uc-core
 * replaces the whole object, so capacity_kg_h and other keys are carried over). No steps: the
 * step_codes key is dropped (an empty list would still make it a machine). undefined: nothing to
 * send (no steps now, none before).
 */
export function specificationsFor(stepCodes: readonly string[], current: Specifications | null | undefined): Specifications | undefined {
  const { step_codes: had, ...rest } = current ?? {}
  if (stepCodes.length) return { ...rest, step_codes: [...stepCodes] }
  return had === undefined ? undefined : rest
}

/**
 * Form → request body. New: empty fields are left out, category MEASUREMENT (gate equipment) or
 * PROCESSING (a machine), no isActive (the create DTO does not take it — new equipment is
 * active). Edit: emptied text and date fields are sent as null so they clear. The place cannot be
 * cleared (uc-core takes a location id only), so an empty place on edit is left out. Steps go
 * into specifications.step_codes, merged with `currentSpecifications` (the row's).
 */
export function equipmentBody(f: EquipmentForm, isNew: boolean, currentSpecifications?: Specifications | null): EquipmentBody {
  const opt = (v: string) => (v.trim() ? v.trim() : isNew ? undefined : null)
  const machine = f.gateKind === MACHINE
  const specifications = specificationsFor(machine || !isNew ? f.stepCodes : [], currentSpecifications)
  const body: EquipmentBody = {
    code: f.code.trim(),
    name: f.name.trim(),
    ...(isNew ? { category: machine ? ('PROCESSING' as const) : ('MEASUREMENT' as const) } : {}),
    ...(f.gateKind && f.gateKind !== MACHINE ? { gateKind: f.gateKind } : {}),
    serialNo: opt(f.serialNo),
    calibratedAt: opt(f.calibratedAt),
    calibrationDueAt: opt(f.calibrationDueAt),
    legalMetrologyCertNo: opt(f.legalMetrologyCertNo),
    stampedAt: opt(f.stampedAt),
    stampDueAt: opt(f.stampDueAt),
    ...(f.locationId ? { locationId: f.locationId } : {}),
    sharedUse: f.sharedUse,
    ...(isNew ? {} : { isActive: f.isActive }),
    ...(specifications ? { specifications } : {}),
  }
  for (const k of Object.keys(body) as Array<keyof EquipmentBody>) if (body[k] === undefined) delete body[k]
  return body
}
